/**
 * Questo file definisce il componente WeatherWidget.
 * - È un widget che mostra le condizioni meteorologiche attuali per una città specifica (Roma).
 * - Effettua una chiamata all'API di OpenWeather per recuperare i dati.
 * - Gestisce autonomamente gli stati di caricamento, errore e successo.
 * - Mostra un loader durante il fetch, un messaggio di errore in caso di fallimento,
 * e i dati meteo (icona, temperatura, città) in caso di successo.
 * - Richiede una API key di OpenWeather per funzionare, con un controllo per evitare chiamate
 * se la chiave non è configurata.
 */

import React, { useState, useEffect } from 'react';
import './WeatherWidget.css';

/**
 * Componente funzionale WeatherWidget.
 * @returns {React.ReactElement | null} - Il JSX del widget o null se non ci sono dati.
 */
const WeatherWidget = () => {
    // Stato per memorizzare i dati meteo ricevuti dall'API.
    const [weather, setWeather] = useState(null);
    // Stato per gestire la fase di caricamento della chiamata API.
    const [loading, setLoading] = useState(true);
    // Stato per memorizzare eventuali errori durante la chiamata API.
    const [error, setError] = useState(null);

    // ATTENZIONE: Questa API Key è un segnaposto e dovrebbe essere gestita
    // in modo sicuro, ad esempio tramite variabili d'ambiente.
    const apiKey = '882ac63821d47931669bb3700004ef63';
    const city = 'Rome,IT'; // Città di riferimento per il meteo.
    // URL completo per la chiamata API a OpenWeather.
    const url = `https://api.openweathermap.org/data/2.5/weather?q=${city}&appid=${apiKey}&units=metric&lang=it`;

    // Effetto che viene eseguito al montaggio del componente per avviare il fetch dei dati.
    useEffect(() => {
        // Funzione asincrona per recuperare i dati meteo.
        const fetchWeather = async () => {
            // Controllo di sicurezza: non effettua la chiamata se la API key è un segnaposto.
            if (apiKey === 'YOUR_OPENWEATHER_API_KEY') {
                setError('API Key di OpenWeather non configurata.');
                setLoading(false);
                return;
            }
            
            try {
                // Esegue la chiamata fetch all'URL dell'API.
                const response = await fetch(url);
                // Se la risposta non è 'ok' (es. status 401, 404), solleva un errore.
                if (!response.ok) {
                    throw new Error('Dati meteo non disponibili al momento.');
                }
                // Parse della risposta JSON.
                const data = await response.json();
                // Aggiorna lo stato con i dati ricevuti.
                setWeather(data);
            } catch (err) {
                // In caso di errore (nella chiamata o nel parsing), aggiorna lo stato di errore.
                setError(err.message);
            } finally {
                // In ogni caso (successo o fallimento), imposta 'loading' a false.
                setLoading(false);
            }
        };

        fetchWeather();
    }, [url, apiKey]); // Le dipendenze dell'effetto. Si rieseguirà se 'url' o 'apiKey' cambiano.

    /**
     * Funzione per costruire l'URL dell'icona meteo fornita da OpenWeather.
     * @param {string} iconCode - Il codice dell'icona ricevuto dall'API.
     * @returns {string | null} - L'URL completo dell'immagine o null.
     */
    const getWeatherIcon = (iconCode) => {
        if (!iconCode) return null;
        return `https://openweathermap.org/img/wn/${iconCode}@2x.png`;
    };

    // Sub-componente per lo stato di caricamento.
    const LoadingState = () => (
        <div className="weather-widget-placeholder">
            <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Caricamento meteo...</span>
        </div>
    );

    // Sub-componente per lo stato di errore.
    const ErrorState = () => (
        <div className="weather-widget-placeholder error">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span>{error}</span>
        </div>
    );

    // Logica di renderizzazione condizionale.
    if (loading) return <LoadingState />; // Mostra il loader.
    if (error) return <ErrorState />; // Mostra il messaggio di errore.
    if (!weather) return null; // Non renderizza nulla se non ci sono dati.

    // Renderizzazione del widget in caso di successo.
    return (
        <div className="weather-widget">
            <div className="weather-icon">
                <img 
                    src={getWeatherIcon(weather.weather[0].icon)} 
                    alt={weather.weather[0].description}
                    // Gestisce il caso in cui l'immagine non si carichi.
                    onError={(e) => { e.target.style.display = 'none' }}
                />
            </div>
            <div className="weather-info">
                {/* Arrotonda la temperatura e la visualizza. */}
                <div className="weather-temp">{Math.round(weather.main.temp)}°C</div>
                <div className="weather-city">Roma</div>
            </div>
            <div className="weather-description">
                {/* Mostra la descrizione testuale del meteo (es. "poche nuvole"). */}
                {weather.weather[0].description}
            </div>
        </div>
    );
};

export default WeatherWidget;